import AsyncStorage from '@react-native-async-storage/async-storage';
import { createLogger } from '@/lib/logger';

const log = createLogger('ONBOARD_STATE');

const STORAGE_KEY = 'onboarding:state';

export interface OnboardingState {
  currentStep: string;
  username?: { firstName: string; lastName: string; handle: string };
  details?: { gender: string; unit: string; dob: string; bodyweight: string; ethnicity: string };
  training?: { selectedIds: string[] };
  homeGym?: { gymId: string; gymName: string };
}

/** Save partial onboarding data for a specific step */
export async function saveOnboardingStep<K extends keyof Omit<OnboardingState, 'currentStep'>>(
  userId: string,
  step: K,
  data: OnboardingState[K],
): Promise<void> {
  try {
    const existing = await loadOnboardingState(userId);
    const updated: OnboardingState = {
      ...existing,
      currentStep: step as string,
      [step]: data,
    };
    await AsyncStorage.setItem(`${STORAGE_KEY}:${userId}`, JSON.stringify(updated));
  } catch (err) {
    log.warn('Failed to save onboarding step', step, err);
  }
}

/** Load saved onboarding state (returns null if none) */
export async function loadOnboardingState(userId: string): Promise<OnboardingState | null> {
  try {
    const raw = await AsyncStorage.getItem(`${STORAGE_KEY}:${userId}`);
    return raw ? (JSON.parse(raw) as OnboardingState) : null;
  } catch (err) {
    log.warn('Failed to load onboarding state', err);
    return null;
  }
}

/** Clear saved onboarding state (call after onboarding completes) */
export async function clearOnboardingState(userId: string): Promise<void> {
  try {
    await AsyncStorage.removeItem(`${STORAGE_KEY}:${userId}`);
  } catch (err) {
    log.warn('Failed to clear onboarding state', err);
  }
}
